import { Suspense, useContext, useEffect, useMemo } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { routes } from "./Routes";
import { ThemeContext } from "../common/themeContext";
import { CommonMessageProvider } from "../common/CommonMessage";
import PrivateLayout from "./PrivateLayout";
import PublicLayout from "./PublicLayout";
import { useAdminPermissions } from "../hooks/useAdminPermissions";
import { loginSliceAction } from "../redux/action/loginSlice";
import { getAdminProfileAction } from "../redux/action/getAdminProfileSlice";
import {
  registerForbiddenNavigation,
  registerSessionReset,
  setForbiddenDefaultRoute,
} from "../services/forbiddenNavigation";

const AppRoutes = () => {
  const { theme, brandId } = useContext(ThemeContext);
  const isPrimeCareTheme = theme === "primeCare";
  const availableRoutes = useMemo(
    () =>
      routes.filter((route) => !route.isPrimeCareOnly || isPrimeCareTheme),
    [isPrimeCareTheme]
  );

  const privateRoutes = useMemo(
    () => availableRoutes.filter((route) => route.isPrivate),
    [availableRoutes]
  );

  const publicLayoutRoutes = useMemo(
    () => availableRoutes.filter((route) => !route.isPrivate && route.isLayout),
    [availableRoutes]
  );

  const publicRoutes = useMemo(
    () => availableRoutes.filter((route) => !route.isPrivate && !route.isLayout),
    [availableRoutes]
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { defaultRoute, isPending } = useAdminPermissions();

  const token = localStorage.getItem("token");
  const isChildRoute = availableRoutes.some(
    (route) => route.isChild && route.path === location.pathname
  );
  const hasChildAccess = Boolean(location.state?.fromChildRoute);

  useEffect(() => {
    registerForbiddenNavigation(navigate);
    return () => registerForbiddenNavigation(null);
  }, [navigate]);

  useEffect(() => {
    const resetSession = () => {
      dispatch(loginSliceAction.loginDetailsSliceReset());
      dispatch(getAdminProfileAction.getAdminProfileInfoReset());
    };

    registerSessionReset(resetSession);
    return () => registerSessionReset(null);
  }, [dispatch]);

  useEffect(() => {
    if (defaultRoute) {
      setForbiddenDefaultRoute(defaultRoute);
    }
  }, [defaultRoute]);

  useEffect(() => {
    const isOnRoot = location.pathname === "/login" || location.pathname === "/";
    const isOnPrivatePath = availableRoutes.some(
      (route) => route.isPrivate && route.path === location.pathname
    );

    if (token && isOnRoot) {
      if (isPending) {
        return;
      }

      if (defaultRoute) {
        navigate(defaultRoute, { replace: true });
      }
    } else if (!token && isOnPrivatePath) {
      navigate("/");
    } else if (token && isChildRoute && !hasChildAccess) {
      if (isPrimeCareTheme) {
        navigate("/ai-campaigns/manage-material");
      } else {
        navigate(defaultRoute ?? "/", { replace: true });
      }
    }
  }, [
    token,
    location.pathname,
    navigate,
    isChildRoute,
    hasChildAccess,
    availableRoutes,
    defaultRoute,
    isPending,
  ]);

  const renderRouteElement = (route) => {
    if (route.isChild && !hasChildAccess) {
      const fallbackPath = isPrimeCareTheme
        ? "/ai-campaigns/manage-material"
        : defaultRoute ?? "/";
      return <Navigate to={fallbackPath} replace />;
    }

    return <route.element />;
  };

  return (
    <div className={theme === "healthCare" ? "healthcare" : "primecare"}>
      <CommonMessageProvider>
        <Suspense>
          <Routes>
            <Route element={<PrivateLayout />}>
              {privateRoutes.map((route, index) => (
                <Route
                  path={route.path}
                  key={`private_${route.path}_${index}`}
                  element={renderRouteElement(route)}
                />
              ))}
            </Route>

            <Route element={<PublicLayout />}>
              {publicLayoutRoutes.map((route, index) => (
                <Route
                  path={route.path}
                  key={`public_layout_${route.path}_${index}`}
                  element={renderRouteElement(route)}
                />
              ))}
            </Route>

            {publicRoutes.map((route, index) => (
              <Route
                path={route.path}
                key={`public_${route.path}_${index}`}
                element={renderRouteElement(route)}
              />
            ))}
          </Routes>
        </Suspense>
      </CommonMessageProvider>
    </div>
  );
};

export default AppRoutes;
